/**
 * View component for /metrics
 *
 * Generic metric list view. Defaults to 'all' with:
 * this.props.dispatch(metricActions.fetchListIfNeeded());
 *
 * NOTE: More list examples are:
 * this.props.dispatch(metricActions.fetchListIfNeeded("all"));
 * this.props.dispatch(metricActions.fetchListIfNeeded()).then((data) => {
 *   console.log("DATA", data);
 * });
 * this.props.dispatch(metricActions.fetchListIfNeeded("_workout"));
 * this.props.dispatch(metricActions.fetchListIfNeeded("_section", "1234"));
 * this.props.dispatch(metricActions.fetchList("_section", "3456", "78910")).then(() => {
 *   this.props.dispatch(metricActions.invalidateList("_section", "3456", "78910"));
 * });
 * this.props.dispatch(metricActions.setFilter({test: 2}));
 * this.props.dispatch(metricActions.setFilter({test: 2}, "_section", "1234"));
 * this.props.dispatch(metricActions.setPagination({test: 1}, "_section", "1234"));
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

// import actions
import * as metricActions from '../metricActions';
import * as metricCategoryActions from '../../metricCategory/metricCategoryActions';
import * as contentModuleActions from '../../contentModule/contentModuleActions';

// import global components
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from "../../../global/components/routing/Breadcrumbs.js.jsx";
import Pagination from "../../../global/components/helpers/Pagination.js.jsx";
import { SearchInput } from '../../../global/components/forms';
import CheckboxInput from '../../../global/components/forms/CheckboxInput.js.jsx';
import ObjectListComparator from '../../../global/components/forms/ObjectListComparator.js.jsx';
import SelectFromArray from '../../../global/components/forms/SelectFromArray.js.jsx';

// import metric components
import AuthoringLayout from '../../admin/components/AuthoringLayout.js.jsx';
import MetricListItem from '../components/MetricListItem.js.jsx';

// import util components
import routeUtils from "../../../global/utils/routeUtils";

class MetricList extends Base {
  constructor(props) {
    super(props);
    this.state = {
      query: {
        page: '1'
        , per: '50'
        , queryText: ''
        , sortBy: 'title'
        , survey: 'false' // has to be string form for search params
        , likelihoodDeviation: []
        , impactNonCompliance: []
        , _metricCategory: []
      }
      , nextQuery: {}
      , formHelpers: {
        impactNonCompliance_1: false
        , impactNonCompliance_2: false
        , impactNonCompliance_3: false
        , likelihoodDeviation_1: false
        , likelihoodDeviation_2: false
        , likelihoodDeviation_3: false
        , survey: false
      }
      , showMetricsFilterOptions: false
      , typing: false 
      , typingTimeout: null
    };
    this._bind(
      '_handleQuery'
      , '_handleFormChange'
      , '_handleSetPagination'
      , '_handleSort'
      , '_setNewFilter'
      , '_setPerPage'
      , '_toggleShowMetricFilterOptions'
    );
  }

  componentDidMount() {
    // fetch a list of your choice
    // this.props.dispatch(metricActions.fetchListIfNeeded("search", routeUtils.queryStringFromObject(this.state.query))); // defaults to 'all'
    this.props.dispatch(metricCategoryActions.fetchListIfNeeded('all'))
    this._setNewFilter()
    window.location.assign(`${authoringUrl}/metrics`); // should i remove everything else in this component? 
  }

  _handleSort(sortBy) {
    const { dispatch } = this.props;
    let newQuery = _.cloneDeep(this.state.query);
    newQuery.sortBy = sortBy;
    newQuery.page = "1"; // reset our pagination
    this.setState({query: newQuery});
    dispatch(metricActions.fetchListIfNeeded("search", routeUtils.queryStringFromObject(newQuery)));
  }

  _setPerPage(per) {
    const { dispatch } = this.props;
    let newQuery = _.cloneDeep(this.state.query);
    newQuery.page = '' + 1;
    newQuery.per = '' + per
    this.setState({query: newQuery});
    dispatch(metricActions.fetchListIfNeeded("search", routeUtils.queryStringFromObject(newQuery)));
  }

  _handleSetPagination(newPagination) {
    const { dispatch } = this.props;
    let newQuery = _.cloneDeep(this.state.query);
    newQuery.page = '' + newPagination.page;
    newQuery.per = '' + newPagination.per
    this.setState({query: newQuery});
    dispatch(metricActions.fetchListIfNeeded("search", routeUtils.queryStringFromObject(newQuery)));
  }

  _handleFormChange(e) {
    let nextState = _.update( this.state, e.target.name, () => {
      return e.target.value;
    });
    // console.log(nextState)
    this.setState(nextState);
  }

  _handleQuery(e) {
    console.log('handlequery');
    const { dispatch } = this.props;
    let newQuery = _.cloneDeep(this.state.query);
    newQuery.queryText = e.target.value.toLowerCase();
    newQuery.page = "1"; // reset our pagination
    if(this.state.typingTimeout) {
      clearTimeout(this.state.typingTimeout);
    }
    this.setState({
      query: newQuery
      , nextQuery: newQuery
      , typing: false 
      , typingTimeout: setTimeout(() => {
        dispatch(metricActions.fetchListIfNeeded("search", routeUtils.queryStringFromObject(newQuery)));
      }, 400)
    });
  }

  _setNewFilter() {
    const { formHelpers, nextQuery } = this.state;
    // console.log('Setting New Filter...')
    let newQuery = _.cloneDeep(nextQuery);
    if(formHelpers.impactNonCompliance_1) {
      if(!newQuery.impactNonCompliance) {
        newQuery.impactNonCompliance = []
      }
      newQuery.impactNonCompliance.push(1);
    }
    if(formHelpers.impactNonCompliance_2) {
      if(!newQuery.impactNonCompliance) {
        newQuery.impactNonCompliance = []
      }
      newQuery.impactNonCompliance.push(2);
    }
    if(formHelpers.impactNonCompliance_3) {
      if(!newQuery.impactNonCompliance) {
        newQuery.impactNonCompliance = []
      }
      newQuery.impactNonCompliance.push(3);
    }
    if(formHelpers.likelihoodDeviation_1) {
      if(!newQuery.likelihoodDeviation) {
        newQuery.likelihoodDeviation = []
      }
      newQuery.likelihoodDeviation.push(1);
    }
    if(formHelpers.likelihoodDeviation_2) {
      if(!newQuery.likelihoodDeviation) {
        newQuery.likelihoodDeviation = []
      }
      newQuery.likelihoodDeviation.push(2);
    }
    if(formHelpers.likelihoodDeviation_3) {
      if(!newQuery.likelihoodDeviation) {
        newQuery.likelihoodDeviation = []
      }
      newQuery.likelihoodDeviation.push(3);
    }
    
    newQuery.survey = "" + formHelpers.survey
    newQuery.page = "1"; // reset our pagination
    this.setState({
      query: newQuery
    });
    // console.log("newQuery", newQuery);
    this.props.dispatch(metricActions.fetchListIfNeeded("search", routeUtils.queryStringFromObject(newQuery)));
  }

  _toggleShowMetricFilterOptions() {
    this.setState({ 
      showMetricsFilterOptions: !this.state.showMetricsFilterOptions
      , showUserFilterOptions: false
    })  
  }

  render() {
    const { 
      location
      , loggedInUser
      , metricStore
      , metricCategoryStore
     } = this.props;
     const {
       formHelpers
       , showMetricsFilterOptions
       , query
       , nextQuery
     } = this.state;

    /**
     * Retrieve the list information and the list items for the component here.
     *
     * NOTE: if the list is deeply nested and/or filtered, you'll want to handle
     * these steps within the mapStoreToProps method prior to delivering the
     * props to the component.  Othwerwise, the render() action gets convoluted
     * and potentially severely bogged down.
     */

    // get the metricList meta info here so we can reference 'isFetching'
    const queryString = routeUtils.queryStringFromObject(this.state.query);

    const metrics = metricStore.util.getList("search", queryString);
    const metricList = metricStore.lists.search ? metricStore.lists.search[queryString] : {};
    const metricCategories = metricCategoryStore.util.getList('all') || []

    /**
     * NOTE: isEmpty is is usefull when the component references more than one
     * resource list.
     */
    const isEmpty = !metricList || !metricList.items || metricList.didInvalidate;

    return (
      <AuthoringLayout>
        <div className="flex">
          <section className="section">
            <div className="yt-container">
              <h1> Metric List
                {/* <Link className="yt-btn create u-pullRight" to={'/admin/authoring/metrics/new'}>
                  <img className="-icon" src={'/img/create-white.png'}></img>
                </Link> */}
              </h1>
              <hr/>
              <button className="yt-btn info" onClick={this._toggleShowMetricFilterOptions}>{showMetricsFilterOptions ? 'Hide Filter Options' : 'Filter Options'}</button>
              <div className="yt-toolbar">
                { showMetricsFilterOptions ?
                  <div>
                    <hr/>
                    { loggedInUser.admin ?
                      <CheckboxInput
                        change={this._handleFormChange}
                        checked={formHelpers.survey}
                        label="Show Survey Metrics"
                        name="formHelpers.survey"
                        value={formHelpers.survey || false}
                      />
                    : null
                    }
                    <ObjectListComparator
                      // addRemoveAll={true}
                      addLabel="Include All"
                      availableLabel="Available List"
                      displayKey="title"
                      name="nextQuery._metricCategory"
                      label={"Filter By Metric Category"}
                      removeLabel="Exclude All"
                      selected={nextQuery._metricCategory || []}
                      selectedLabel="Selected (Leave Empty for All)"
                      change={this._handleFormChange}
                      items={
                        [{title: "*NO CATEGORY*", _id: "none"}].concat(metricCategories)
                      }
                      valueKey="_id"
                    />
                    <p>
                      <strong>Filter by Likelihood of Deviation</strong>
                    </p>
                    <CheckboxInput
                      change={this._handleFormChange}
                      checked={formHelpers.likelihoodDeviation_1}
                      label="LDO scores of 1"
                      name="formHelpers.likelihoodDeviation_1"
                      value={formHelpers.likelihoodDeviation_1 || false}
                    />
                    <CheckboxInput
                      change={this._handleFormChange}
                      checked={formHelpers.likelihoodDeviation_2}
                      label="LDO scores of 2"
                      name="formHelpers.likelihoodDeviation_2"
                      value={formHelpers.likelihoodDeviation_2 || false}
                    />
                    <CheckboxInput
                      change={this._handleFormChange}
                      checked={formHelpers.likelihoodDeviation_3}
                      label="LDO scores of 3"
                      name="formHelpers.likelihoodDeviation_3"
                      value={formHelpers.likelihoodDeviation_3 || false}
                    />
                    <p>
                      <strong>Filter by Impact of NonCompliance</strong>
                    </p>
                    <CheckboxInput
                      change={this._handleFormChange}
                      checked={formHelpers.impactNonCompliance_1}
                      label="INC scores of 1"
                      name="formHelpers.impactNonCompliance_1"
                      value={formHelpers.impactNonCompliance_1 || false}
                    />
                    <CheckboxInput
                      change={this._handleFormChange}
                      checked={formHelpers.impactNonCompliance_2}
                      label="INC scores of 2"
                      name="formHelpers.impactNonCompliance_2"
                      value={formHelpers.impactNonCompliance_2 || false}
                    />
                    <CheckboxInput
                      change={this._handleFormChange}
                      checked={formHelpers.impactNonCompliance_3}
                      label="INC scores of 3"
                      name="formHelpers.impactNonCompliance_3"
                      value={formHelpers.impactNonCompliance_3 || false}
                    />
                    <button className="yt-btn small info" onClick={this._setNewFilter}>Set Metric Filters</button>
                    <hr/>
                  </div>
                  :
                  null
                }
                <div className="yt-tools right">
                  <div className="search">
                    <SearchInput
                      change={this._handleQuery}
                      name="query"
                      placeholder="Search..."
                      required={false}
                      value={this.state.query.queryText}
                      />
                  </div>
                </div>
                { isEmpty ?
                  (metricList && metricList.isFetching ? 
                    <div className="u-centerText">
                      <div className="loading"></div>
                    </div>
                    :
                    <h2>Empty.</h2>
                  )
                  :
                  <div style={{ opacity: metricList.isFetching ? 0.5 : 1 }}>
                    <div className="content-container">
                      <div className="table-wrapper">
                        <table className="yt-table striped">
                          <caption>
                            { metricList.filter && metricList.filter.queryText && metricList.filter.queryText.length > 0 ?
                              <span>Filtered Metrics &mdash; {metricList.items.length} of {metricList.pagination.total}</span>
                              :
                              <span>Filtered Metrics &mdash; {metricList.pagination ? metricList.pagination.total : 0}</span>
                            }
                            <div className="per-page-select u-pullRight">
                              <label>Show per page: </label>
                              <select
                                name="numPerPage"
                                onChange={(e) => this._setPerPage(e.target.value)}
                                value={this.state.per}
                                >
                                <option value={25}> 25 </option>
                                <option value={50}> 50 </option>
                                <option value={100}> 100 </option>
                              </select>
                            </div>
                          </caption>
                          <thead>
                            <tr>
                              <th className="_10 sortable" onClick={()=> this._handleSort(metricList.filter && metricList.filter.sortBy === 'order' ? '-order' : 'order')}>
                                Order
                                {metricList.filter && metricList.filter.sortBy === 'order' ?
                                  <i className="ion ion-ios-arrow-down" />
                                  : metricList.filter && metricList.filter.sortBy === '-order' ?
                                  <i className="ion ion-ios-arrow-up" />
                                  : null
                                }
                              </th>
                              <th className="_70 sortable" onClick={()=> this._handleSort(metricList.filter && metricList.filter.sortBy === 'title' ? '-title' : 'title')}>
                                Metric
                                {metricList.filter && metricList.filter.sortBy === 'title' ?
                                  <i className="ion ion-ios-arrow-down" />
                                  : metricList.filter && metricList.filter.sortBy === '-title' ?
                                  <i className="ion ion-ios-arrow-up" />
                                  : null
                                }
                              </th>
                              <th className="_10 sortable" onClick={()=> this._handleSort(metricList.filter && metricList.filter.sortBy === 'updated' ? '-updated' : 'updated')}>
                                Last Modified
                                {metricList.filter && metricList.filter.sortBy === 'updated' ?
                                  <i className="ion ion-ios-arrow-down" />
                                  : metricList.filter && metricList.filter.sortBy === '-updated' ?
                                  <i className="ion ion-ios-arrow-up" />
                                  : null
                                }
                              </th>
                            </tr>
                          </thead>
                          <tbody className="aut-metrics-table-body">
                            {metrics.map((metric, i) =>
                              <MetricListItem key={metric._id + '_' + i} metric={metric} />
                            )}
                          </tbody>
                        </table>
                        { metricList.items.length === 0 ?
                          null
                          :
                          <Pagination
                            classes="u-centerText"
                            totalPages={metricList.pagination ? Math.floor(metricList.pagination.total / metricList.pagination.per) + 1 : 1}
                            pagination={metricList.pagination ? metricList.pagination : {page: 1, per: 50}}
                            setPagination={this._handleSetPagination}
                          />
                        }
                      </div>
                    </div>
                  </div>
                }
              </div>
            </div>
          </section>
        </div>
      </AuthoringLayout>
    )
  }
}

MetricList.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  return {
    loggedInUser: store.user.loggedIn.user
    , metricStore: store.metric
    , metricCategoryStore: store.metricCategory
  }
}

export default withRouter(
    connect(
    mapStoreToProps
  )(MetricList)
);



// WEBPACK FOOTER //
// modules/metric/views/MetricList.js.jsx